import React, {FunctionComponent, useEffect, useState} from 'react';
import {RootState} from "../../../../Config/Store";
import {getEntities, PowerIrradiancia, reset} from './PowerVSIrradianceChartReducer';
import {useAppDispatch, useAppSelector} from "../../../../Config/Hooks";
import {downloadFile} from "../../../../Utils/DownloadUtils";
import IconButton from "@mui/material/IconButton";
import {Box, Divider, Stack, Typography, useTheme} from "@mui/material";
import {addMinutes, format, parseISO} from 'date-fns';
import {isArray} from "lodash";
import {calculatePowerAxisMax, roundNextHundred, truncateNumber} from "../../../../Utils/NumberUtil";
import {FindPowerStationInformationReactQuery} from "../PowerStationInformation/PowerStationInformationReactQuery";
import {AUTHORITIES} from "../../../../Config/Constants";
import {
    ACard,
    AChartX,
    Period,
    PeriodSelector,
    Skeleton,
    XAxis,
    YAxis
} from "@atiautomacao/ati-ui-library"
import {checkDiffDays} from "../../../../Utils/DataUitils";
import DataNotFound from "../../../../Shared/Components/DataNotFoundMessage";
import useInterval from "../../../../Shared/Hooks/useInterval";
import {hasPermission} from "../../../../Shared/Auth/AuthenticationUtil";
import { getChartXOptions } from '../../../../Utils/ChartXOption';
import {LocalDateTimeFormatISO} from "../../../../Utils/DateFormatPatternUtils";
import { SimCardDownloadOutlined } from '@mui/icons-material';
import {useSnackbar} from "notistack";
import {CSV_EXPORT_ERROR_MESSAGE, CSV_EXPORT_NO_DATA_MESSAGE} from "../../../../Utils/CsvExportMessages";

interface SeriesOption {
    name: string;
    type: string;
    unity: string;
    yAxisIndex?: number | undefined;
    data: any[];
    date?: any;
    showSymbol?: boolean;
}

const PowerVSIrradianceChart: FunctionComponent<{ powerPlant: any; }> = ({powerPlant}) => {
    const dispatch = useAppDispatch();
    const {enqueueSnackbar} = useSnackbar();
    const account = useAppSelector(state => state.authentication.account);
    const isAuthorizedToSearchByCsv = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.VIEW_CSV_EXPORT, AUTHORITIES.EXPORT_POWER_VC_IRRADIANCE, AUTHORITIES.CSV_EXPORT_POWER_VC_IRRADIANCE
    ]);
    const openSubMenu = useAppSelector((state: any) => state.layout.openSubMenu);
    const PowerIrradianceEntities: Array<PowerIrradiancia> = useAppSelector((state: RootState) => state.powerIrradianceChart.entities);
    const theme = useTheme();
    const { loading} = useAppSelector((state: RootState) => state.powerIrradianceChart);
    // Setting range start period
    const initialPeriod: Period = {
        groupBy: "day",
        fromDateTime: new Date(),
        toDateTime: new Date()
    };
    const [period, setPeriod] = useState<Period>(initialPeriod);
    const [xAxis, setXAxis] = useState<XAxis>();
    const [yAxis, setYAxis] = useState<Array<YAxis>>(Array<YAxis>());
    const [series, setSeries] = useState<Array<SeriesOption>>(Array<SeriesOption>());
    const [showGraph, setShowGraph] = useState(false);
    const userConfig = useAppSelector((state: RootState) => state.userConfigurarion);
    const {data: dataInfo, isLoading: isLoadingStationInfo} = FindPowerStationInformationReactQuery(
        powerPlant?.id,
        { refetchOnMount: true }
    );
    const maxPower = dataInfo?.data?.maxPower;

    const isDay = (group:string)=>{
        return group === "day"
    }

    const callback = (params:any) => {
        let formatDate = period && period.groupBy.valueOf() === "week" ? 'dd/MM HH:mm' : 'HH:mm'
        let dateStr = format(new Date(params[0].axisValue), formatDate);

        let result = dateStr + '<br/>';
        params.forEach(function (item: any) {
            result += item.marker + " " + item.seriesName + "&nbsp;&nbsp;&nbsp;&nbsp;" + "<strong>" + item.value[1] + " " + (item.seriesName == "Irradiância" ? 'W/m²' : 'kW') + "</strong>" + "<br/>";
        });

        return result;
    }

    const adjustDataPoints = (dataPoints: any[]): any[] => {
        if (dataPoints.length === 0) {
            return dataPoints;
        }

        // Parseia o primeiro e último dateTime para objetos Date
        const firstDateTime = parseISO(dataPoints[0].dateTime);
        const lastDateTime = parseISO(dataPoints[dataPoints.length - 1].dateTime);

        // Horários limite
        const startTime = new Date(firstDateTime);
        startTime.setHours(4, 0, 0, 0); // 4:00
        const endTime = new Date(lastDateTime);
        endTime.setHours(20, 0, 0, 0); // 20:00

        let newData = [...dataPoints]
        while (firstDateTime > startTime) {
            const newDateTime = addMinutes(firstDateTime, -5);
            newData.unshift({
                dateTime: format(newDateTime, 'yyyy-MM-dd HH:mm:ss.S'),
                activePower: null,
                irradiance: null,
            });
            firstDateTime.setTime(newDateTime.getTime());
        }

        while (lastDateTime < endTime) {
            const newDateTime = addMinutes(lastDateTime, 5);
            newData.push({
                dateTime: format(newDateTime, 'yyyy-MM-dd HH:mm:ss.S'),
                activePower: null,
                irradiance: null,
            });
            lastDateTime.setTime(newDateTime.getTime());
        }

        return newData;
    }

    const hoursToMilliseconds = (hours:number): number => {
        return hours * 60 * 60 * 1000
    };

    const minDate = (minDate:string, group:string) : string | undefined => {
        if(!isDay(group)) return undefined;
        const min = new Date(minDate);
        min.setHours(4, 0, 0, 0); // 4:00
        return format(min, 'yyyy-MM-dd HH:mm:ss.S')
    }

    const maxDate = (maxDate:string, group:string) : string | undefined => {
        if(!isDay(group)) return undefined;
        const max = new Date(maxDate);
        max.setHours(20, 0, 0, 0);
        return format(max, 'yyyy-MM-dd HH:mm:ss.S')
    }



    const findPowerDataByPeriod = () => {
        if(period.groupBy){
            const grouping = period.groupBy.valueOf()
            const newData =  isDay(grouping) ? adjustDataPoints(PowerIrradianceEntities) : PowerIrradianceEntities;

            setXAxis({
                type:'time',
                min: minDate(newData[0]?.dateTime, period.groupBy.valueOf()) ,
                max: maxDate(newData[0]?.dateTime, period.groupBy.valueOf()) ,
                nameLocation: 'middle',
                minInterval: (isDay(grouping) ? hoursToMilliseconds(4) : hoursToMilliseconds(24)),
                axisLabel: {
                    formatter: isDay(grouping) ? '{HH}:{mm}' : '{dd}/{MM}'
                },
                nameGap: 30
            });

            setShowGraph(PowerIrradianceEntities.length > 0);
            const capacityMaxLeftY = calculatePowerAxisMax(maxPower);
            const valueMaxIrradiance = newData.reduce((max, obj) => (obj.irradiance > max.irradiance ? obj : max), newData[0]).irradiance;
            const defaultRightY = valueMaxIrradiance > 1200 ? roundNextHundred(valueMaxIrradiance) : 1200;
            setYAxis([
                {
                    // name: 'kW',
                    max: capacityMaxLeftY,
                    min: 0,
                    interval: roundNextHundred(capacityMaxLeftY / 4),
                    nameLocation: 'middle',
                    nameGap: 50,
                },
                {
                    // name: 'W/m²',
                    max: defaultRightY,
                    min: 0,
                    interval: roundNextHundred(defaultRightY / 4),
                    nameLocation: 'middle',
                    nameGap: 40,
                    position: "right",
                }
            ])
            setSeries([
                    {
                        name: 'Potência AC',
                        data: Array.isArray(newData) ? newData.map((item) => item.activePower != null ? [item.dateTime ? item.dateTime : "", truncateNumber(item.activePower, 2)] : [item.dateTime ? item.dateTime : "", "-"]) : [],
                        type: 'line',
                        unity: 'Kw',
                        showSymbol: false
                    },
                    {
                        name: 'Irradiância',
                        data: Array.isArray(newData) ? newData.map((item) => item.irradiance != null ?  [item.dateTime ? item.dateTime : "", truncateNumber(item.irradiance, 2)]: [item.dateTime ? item.dateTime : "","-"]) : [],
                        type: 'line',
                        unity: 'W/m²',
                        showSymbol: false,
                        yAxisIndex: 1,
                    }
                ]
            );
        }

    }

    useEffect(() => {
        // dispatch(reset())
        if(checkDiffDays(period.toDateTime, period.fromDateTime) > 0){
            dispatch(getEntities({period: period, idPowerPlant: powerPlant.id}));
        }
    }, [period, powerPlant, dispatch]);

    useInterval(() => {
        if (checkDiffDays(period.toDateTime, period.fromDateTime) > 0) {
            dispatch(getEntities({period: period, idPowerPlant: powerPlant.id}));
        }
    }, userConfig.mainUpdateInterval) // 5 minutes

    useEffect(() => {
        if (isArray(PowerIrradianceEntities) && PowerIrradianceEntities.length > 0 && period.groupBy && !isLoadingStationInfo) {
            findPowerDataByPeriod();
        } else if (!isLoadingStationInfo) {
            dispatch(reset())
            setSeries([])
            setXAxis(undefined)
            setYAxis([])
        }
    }, [PowerIrradianceEntities, maxPower, powerPlant?.id, isLoadingStationInfo, period.groupBy]);

    const handleDownloadCSV = () => {
        if (!isArray(PowerIrradianceEntities) || PowerIrradianceEntities.length === 0) {
            enqueueSnackbar(CSV_EXPORT_NO_DATA_MESSAGE, {variant: "info"});
            return;
        }

        const params = {
            powerStationId: powerPlant.id,
            startDateTime: LocalDateTimeFormatISO(period.fromDateTime ? period.fromDateTime : new Date()),
            endDateTime: LocalDateTimeFormatISO(period.toDateTime ? period.toDateTime : new Date())
        }
        // O gráfico sempre consulta dados por hora (Dia e 7 Dias), então o CSV usa o mesmo endpoint
        downloadFile(
            'api/power-irradiance/csv/hour',
            params,
            'potencia_vs_irradiancia.csv',
            undefined,
            () => enqueueSnackbar(CSV_EXPORT_ERROR_MESSAGE, {variant: "error"}),
            () => enqueueSnackbar(CSV_EXPORT_NO_DATA_MESSAGE, {variant: "info"})
        );
    };

    // @ts-ignore
    const option: AChartXProps['option'] = {
        ...getChartXOptions({name:"energia-gerada",date:new Date(new Date())}),
        color: ['rgb(25, 118, 210)', 'rgb(255,198,10)'],
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'cross',
                label: {
                    formatter: function (params: any) {
                        const formatDate = period.groupBy.valueOf() === "week" ? "dd/MM HH:mm" : "HH:mm"

                        if (params.axisDimension === 'x') {
                            return format(new Date(params.value), formatDate);
                        }
                        if (params.axisDimension === 'y') {
                            return truncateNumber(params.value,2);
                        }
                    }
                }
            },
            formatter: callback
        },
        toolbox: {
            trigger: 'axis',
            axisPointer: {
                type: 'cross',
                label: {
                    backgroundColor: '#6a7985'
                }
            },
            renderMode: 'richText',
            feature: {
                dataZoom: {
                    yAxisIndex: 'none'
                },
                magicType: { show: false, type: ['line', 'bar'] },
                restore: { show: true }
            },
            orient: 'vertical',
        },
        dataZoom: [
            {
                type: 'inside',
                throttle: 50
            },
        ],
        legend: {
            show: true,
            type: 'scroll',
            orient: 'horizontal', // Alterado para orientação horizontal
            bottom: 2, // Posiciona a legenda abaixo do gráfico
            left: 'center', // Centraliza a legenda horizontalmente
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            textStyle: {
                opacity: 1
            },
            itemStyle: {
                opacity: 1
            },
            data: series.map(el => el.name)
        },
        xAxis: xAxis,
        yAxis: yAxis,
        series: series,
    };

    return (
        <ACard
            key={`chart-power-irradiance`}
            styleProps={{
                cardStyle: { height: 463 },
                contentStyle: {padding: '0 16px', height: 408, alignItems: 'center'}
            }}
            title="Potência x Irradiância"
            ariaLabel={"dashboard-station-power-irradiance-chart"}
            headerControlsPosition={"header"}
            headerControls={
                    <PeriodSelector
                        styleProps={{base: {flexDirection: "row", display: "flex", alignItems: "center", padding: '8px 0'}}}
                        mode="hybrid"
                        hideDatetime={false}
                        hideGroup={false}
                        disableHourSelector={true}
                        inputFormat={'dd/MM/yyyy'}
                        periodInterval={{
                            day: {date: period.toDateTime, startTime: "00:00:00", endTime: "23:59:59"},
                            week: {startTime: "00:00:00", endTime: "23:59:59", startInterval: 6}
                        }}
                        period={period}
                        onChange={setPeriod}
                        groupByOptions={["day", "week"]}
                    />
            }
            headerActions={
                <Stack direction="column" spacing={"8px"}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 12px' }}>
                        <IconButton disabled={!isAuthorizedToSearchByCsv} size="small" sx={{ borderRadius: '4px' }} onClick={handleDownloadCSV}>
                            <SimCardDownloadOutlined sx={{ marginRight: '12px' }} />
                            <Typography variant="body2">Baixar como CSV</Typography>

                        </IconButton>
                    </Box>
                    <Divider />
                </Stack>
            }
            
        >
            {
                loading || isLoadingStationInfo ?
                    <Skeleton animation="wave" height={289} variant="rounded" width={'100%'} />
                    :
                    xAxis && yAxis && series && showGraph ?
                        <AChartX
                            key={`chart-power-irradiance-${openSubMenu}`}
                            height={350}
                            width={"100%"}
                            option={option}
                            style={{marginTop: 4}}
                            theme={theme.palette.mode}
                        />
                        :
                        <DataNotFound boxStyle={{height: 350, width: '100%'}}/>
            }
        </ACard>
    )
}

export default PowerVSIrradianceChart;